import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import front from '@/../static/js/proto/CtpFront';
import model from '@/../static/js/proto/Model';
import FavoriteIcon from '@/components/common/FavoriteIcon.vue';

const EXCHANGE_ICON_MAP = {
  上期所: require('@/assets/images/market/shangqi.png'),
  上期能源: require('@/assets/images/market/nengyuan.png'),
  大商所: require('@/assets/images/market/dashuang.png'),
  郑商所: require('@/assets/images/market/zhenshuang.png'),
  中金所: require('@/assets/images/market/zhongjin.png'),
  广期所: require('@/assets/images/market/guangqi.png'),
};

/**
 * 选择合约的键盘
 */
@Component({
  name: 'TargetKeyboard',
  components: {
    FavoriteIcon,
  },
})
export default class TargetKeyboard extends Vue {
  @Prop()
  public exclude: Node | Node[] | ((current: Node) => boolean);
  @Prop()
  public currentTargetId: string;

  public exchangeDefaultProductMap = {
    上期所: 'ag',
    上期能源: 'sc',
    大商所: 'a',
    郑商所: 'AP',
    中金所: 'IC',
    广期所: 'si',
  };

  public contractData = [];
  public currentExchangeName = '自选';
  public currentProductId = '';
  public dominantSuberId = 'target-keyboard-dominant';
  public dominantTargetMap = {};
  public dominantProductMap = {};

  public get selectTarget(): any {
    return this.$store.state.selectTargets;
  }

  public get exchangeTabs() {
    return [
      {
        exchangeName: '自选',
        shortLabel: '自选',
        isFavoriteIcon: true,
      },
      {
        exchangeName: '上期所',
        shortLabel: '上期',
        iconSrc: EXCHANGE_ICON_MAP.上期所,
        isFontIcon: false,
      },
      {
        exchangeName: '上期能源',
        shortLabel: '能源',
        iconSrc: EXCHANGE_ICON_MAP.上期能源,
        isFontIcon: false,
      },
      {
        exchangeName: '大商所',
        shortLabel: '大商',
        iconSrc: EXCHANGE_ICON_MAP.大商所,
        isFontIcon: false,
      },
      {
        exchangeName: '郑商所',
        shortLabel: '郑商',
        iconSrc: EXCHANGE_ICON_MAP.郑商所,
        isFontIcon: false,
      },
      {
        exchangeName: '中金所',
        shortLabel: '中金',
        iconSrc: EXCHANGE_ICON_MAP.中金所,
        isFontIcon: false,
      },
      {
        exchangeName: '广期所',
        shortLabel: '广期',
        iconSrc: EXCHANGE_ICON_MAP.广期所,
        isFontIcon: false,
      },
    ];
  }

  public get showSelectedPanel(): boolean {
    return this.currentExchangeName === '自选';
  }

  public get activeTargetId(): string {
    return String(this.currentTargetId || '');
  }

  public get selectedTargetsView() {
    const rows = Array.isArray(this.selectTarget) ? this.selectTarget : [];
    const targetSet = (front as any).targetSet || {};
    return rows
      .map((item: any) => {
        const targetId = item && item.targetId ? String(item.targetId) : '';
        const target = targetSet[targetId] || {};
        return {
          targetId,
          targetName: item.targetName || target.targetName || targetId,
          isDominant: this.isDominantTargetId(targetId),
        };
      })
      .filter((item) => item.targetId);
  }

  public get filteredProducts() {
    return this.getProductsByExchange(this.currentExchangeName);
  }

  public get currentContracts() {
    return (this.contractData || [])
      .filter((item: any) => item && item.productId === this.currentProductId)
      .map((item: any) => ({
        ...item,
        isDominant: this.isDominantTargetId(item.targetId),
        isActive: String(item.targetId) === this.activeTargetId,
      }));
  }

  public mounted() {
    this.loader_key();
    this.setupDominant();
  }

  @Watch('currentProductId')
  public onCurrentProductIdChange() {
    this.$nextTick(() => {
      this.scrollCurrentProductIntoView();
    });
  }

  @Watch('currentExchangeName')
  public onCurrentExchangeNameChange() {}

  public beforeDestroy() {
    this.teardownDominant();
  }

  public choiceProduceId(productId) {
    this.currentProductId = productId;
  }

  // 键盘选择合约时触发事件
  public choiceTargetId(targetId) {
    this.passValueToP(targetId);
  }

  // 选择交易所
  public choiceExchangeName(item) {
    this.currentExchangeName = item;
    if (item === '自选') {
      this.currentProductId = '';
      return;
    }
    this.syncCurrentProductId(item);
  }

  public loader_key() {
    this.contractData = Array.isArray((front as any).targetList) ? (front as any).targetList : [];
    if (this.syncFromCurrentTarget()) {
      return;
    }
    this.syncDefaultSelection();
  }

  public handleClose() {
    this.$emit('close');
  }
  public passValueToP(targetId) {
    this.$emit('submit', targetId);
    this.$emit('close');
  }

  public isDominantTargetId(targetId: string): boolean {
    return !!(targetId && this.dominantTargetMap[targetId]);
  }

  public onDominantTarget(target) {
    const targetId = target && target.targetId ? String(target.targetId) : '';
    if (!targetId) return;

    const targetInfo = (front as any).targetSet ? (front as any).targetSet[targetId] : null;
    const productId = targetInfo && targetInfo.productId ? String(targetInfo.productId) : '';
    if (!productId) return;

    const oldTargetId = this.dominantProductMap[productId];
    if (oldTargetId && oldTargetId !== targetId) {
      this.$delete(this.dominantTargetMap, oldTargetId);
    }

    this.$set(this.dominantProductMap, productId, targetId);
    this.$set(this.dominantTargetMap, targetId, true);
  }

  public getProductNameStyle(productName: string, customTargetWidth?: number) {
    const name = String(productName || '').trim();
    const visualWidth = this.getProductNameVisualWidth(name);

    // 自选面板名称较长，默认容器可容纳约 6.0 的视觉宽度
    // 品种面板名称较短，为保持视觉一致性，目标宽度设定为 2.2
    const targetWidth = customTargetWidth || 2.2;

    if (visualWidth <= targetWidth) {
      return {};
    }

    const scaleX = targetWidth / visualWidth;

    // 为了保持可读性，Y轴缩放（高度）不应像X轴（宽度）那样剧烈
    let scaleY = 1.0 - (1.0 - scaleX) * 0.5;
    scaleY = Math.max(0.65, Math.min(1.0, scaleY));

    // X轴缩放需要乘上原有的 0.94 基础缩放系数，以保持视觉一致性
    const finalScaleX = scaleX * 0.94;

    return {
      transform: `scale(${finalScaleX}, ${scaleY})`
    };
  }

  private setupDominant() {
    const dominantModel = model as any;
    if (!dominantModel.dominantSet) {
      dominantModel.dominantSet = (front as any).dominantTargetSub({
        suberId: this.dominantSuberId,
        publish: (target) => this.onDominantTarget(target),
      });
      return;
    }

    (front as any).dominantTargetSub({
      suberId: this.dominantSuberId,
      publish: (target) => this.onDominantTarget(target),
    });
    Object.keys(dominantModel.dominantSet).forEach((targetId) => this.onDominantTarget({ targetId }));
  }

  private teardownDominant() {
    (front as any).dominantTargetUnsub(this.dominantSuberId);
  }

  private scrollCurrentProductIntoView() {
    if (this.showSelectedPanel || !this.currentProductId) return;

    const productGrid = this.$refs.productGrid as HTMLElement;
    if (!productGrid) return;

    const cards = Array.from(productGrid.querySelectorAll('.product-card')) as HTMLElement[];
    const activeCard = cards.find((card) => card.dataset.productId === this.currentProductId);
    if (!activeCard) return;

    const cardTop = activeCard.offsetTop;
    const cardBottom = cardTop + activeCard.offsetHeight;
    const viewTop = productGrid.scrollTop;
    const viewBottom = viewTop + productGrid.clientHeight;

    if (cardTop < viewTop) {
      productGrid.scrollTop = Math.max(cardTop - 8, 0);
      return;
    }

    if (cardBottom > viewBottom) {
      productGrid.scrollTop = Math.max(cardBottom - productGrid.clientHeight + 8, 0);
    }
  }

  private getProductsByExchange(exchangeName: string) {
    const seen = {};
    return (this.contractData || []).filter((item: any) => {
      if (!item || item.exchangeName !== exchangeName || !item.productId || seen[item.productId]) {
        return false;
      }
      seen[item.productId] = true;
      return true;
    });
  }

  private syncFromCurrentTarget(): boolean {
    const targetId = this.activeTargetId;
    if (!targetId) return false;

    if (this.isSelectedTarget(targetId)) {
      this.currentExchangeName = '自选';
      this.currentProductId = '';
      return true;
    }

    const targetSet = (front as any).targetSet || {};
    const target = targetSet[targetId];
    if (!target || !target.exchangeName || !target.productId) {
      return false;
    }

    this.currentExchangeName = target.exchangeName;
    this.currentProductId = target.productId;
    return true;
  }

  private syncCurrentProductId(exchangeName: string) {
    const products = this.getProductsByExchange(exchangeName);
    if (!products.length) {
      this.currentProductId = '';
      return;
    }

    const fallbackProductId = this.exchangeDefaultProductMap[exchangeName];
    const matchedProduct =
      products.find((item: any) => item.productId === this.currentProductId) ||
      products.find((item: any) => item.productId === fallbackProductId) ||
      products[0];

    this.currentProductId = matchedProduct.productId;
  }

  private isSelectedTarget(targetId: string): boolean {
    const rows = Array.isArray(this.selectTarget) ? this.selectTarget : [];
    return rows.some((item: any) => String((item && item.targetId) || '') === targetId);
  }

  private syncDefaultSelection() {
    if (this.selectedTargetsView.length) {
      this.currentExchangeName = '自选';
      this.currentProductId = '';
      return;
    }

    const fallbackExchangeName = this.getFirstAvailableExchangeName();
    if (!fallbackExchangeName) {
      this.currentExchangeName = '自选';
      this.currentProductId = '';
      return;
    }

    this.currentExchangeName = fallbackExchangeName;
    const products = this.getProductsByExchange(fallbackExchangeName);
    this.currentProductId = products.length ? String(products[0].productId || '') : '';
  }

  private getFirstAvailableExchangeName(): string {
    const exchangeTabs = this.exchangeTabs || [];
    const firstExchangeTab = exchangeTabs.find((item: any) => {
      const exchangeName = item && item.exchangeName;
      return exchangeName && exchangeName !== '自选' && this.getProductsByExchange(exchangeName).length;
    });

    return firstExchangeTab ? String(firstExchangeTab.exchangeName) : '';
  }

  private getProductNameVisualWidth(name: string): number {
    return Array.from(name).reduce((total, char) => total + this.getCharVisualWidth(char), 0);
  }

  private getCharVisualWidth(char: string): number {
    if (/[\u4e00-\u9fa5]/.test(char)) return 1;
    if (/[0-9]/.test(char)) return 0.68;
    if (/[A-Z]/.test(char)) return 0.72;
    if (/[a-z]/.test(char)) return 0.6;
    return 0.5;
  }
}



// WEBPACK FOOTER //
// ./src/views/components/TargetKeyboard/TargetKeyboard.ts